/** Fixed parent offers; the native product form owns the only cart request. */
class MonOffresRoutine extends HTMLElement {
  connectedCallback() {
    if (this.abort) return;
    this.abort = new AbortController();
    const options = { signal: this.abort.signal };
    this.form = this.closest('form');
    this.root = this.closest('[data-mmp]');
    this.productForm = this.closest('product-form-component');
    if (!this.form || !this.root) return;
    this.inputs = [...this.querySelectorAll('[data-offre-input]')];
    this.unavailable = new Set(this.inputs.filter(input => input.disabled));
    this.seul = this.inputs.find(input => input.dataset.type === 'seul');
    // La barre collante montre le soin consulté (nom, visuel) : elle affiche son prix
    // et ajoute ce soin seul, sans toucher à l'offre cochée dans le bloc (2026-10-24).
    this.root.addEventListener('click', (event) => {
      if (!event.target.closest('[data-mmp-sticky-cta]')) return;
      if (!this.seul || this.unavailable.has(this.seul) || this.busy || this.selected === this.seul) return;
      this.pointForm(this.seul);
      // Le formulaire lit la variante pendant ce même clic (requestSubmit synchrone).
      setTimeout(() => {
        const id = this.form.querySelector('[name="id"]');
        if (id && this.selected) id.value = this.selected.value;
      }, 0);
      this.restoreTimer = setTimeout(() => this.restoreForm(), 8000);
      this.restorePending = true;
    }, { ...options, capture: true });
    this.addEventListener('change', (event) => {
      if (this.inputs.includes(event.target)) this.select(event.target);
    }, options);
    this.form.addEventListener('submit', (event) => {
      if (this.busy || !this.selected || this.selected.disabled) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }, { ...options, capture: true });
    this.productForm?.addEventListener('shopify:cart:lines-update', (event) => {
      const payload = event;
      if (payload?.context !== 'product' || !payload.promise) return;
      this.setBusy(true);
      this.showError('');
      payload.promise.then((result) => {
        if (result.detail?.didError) this.showError('Cette offre ne peut pas être ajoutée pour le moment. Vérifiez sa disponibilité et réessayez.');
      }).catch(() => {
        this.showError('La connexion a été interrompue. Votre sélection est conservée : réessayez.');
      }).finally(() => {
        this.restoreForm();
        this.setBusy(false);
      });
    }, options);
    this.productForm?.addEventListener('shopify:cart:error', (event) => {
      this.showError(event.code === 'SERVICE_UNAVAILABLE'
        ? 'La connexion a été interrompue. Votre sélection est conservée : réessayez.'
        : 'Cette offre ne peut pas être ajoutée pour le moment. Votre sélection est conservée.');
    }, options);
    // Les anciennes pages coffrets renvoient ici avec ?offre=duo|collection.
    this.offreDemandee = new URLSearchParams(location.search).get('offre');
    const initial = this.inputs.find(input => this.offreDemandee && input.dataset.type === this.offreDemandee && !input.disabled)
      || this.inputs.find(input => input.dataset.type === 'duo' && !input.disabled)
      || this.inputs.find(input => input.dataset.type === 'seul' && !input.disabled);
    this.inputs.forEach(input => { input.checked = false; });
    if (initial) this.select(initial, false);
    else this.sync();
    // Visuels des duos/coffrets au panier (2026-10-29) : chargés d'avance pour que
    // le toast et le tiroir les affichent dès l'ajout, sans attendre le réseau.
    const preload = () => this.inputs.forEach(input => {
      if (input.dataset.type !== 'seul' && input.dataset.image) new Image().src = input.dataset.image;
    });
    if ('requestIdleCallback' in window) requestIdleCallback(preload, { timeout: 3000 });
    else setTimeout(preload, 1500);
  }
  disconnectedCallback() { this.abort?.abort(); this.abort = null; }
  select(input, announce = true) {
    if (this.busy || input.disabled) return;
    this.selected = input;
    this.inputs.forEach(item => {
      const active = item === input;
      item.checked = active;
      const card = item.closest('[data-offre-card]');
      card.classList.toggle('is-selected', active);
      const panel = this.querySelector(`[data-offre-panel][data-type="${item.dataset.type}"]`);
      if (panel) panel.hidden = !active;
    });
    const details = this.querySelector('[data-offres-details]');
    if (details) {
      const panel = details.querySelector(`[data-offre-panel][data-type="${input.dataset.type}"]`);
      const count = panel?.querySelectorAll('.mon-offres-routine__product').length || 0;
      details.hidden = count === 0;
      const counter = details.querySelector('[data-offres-count]');
      if (counter) {
        counter.textContent = `${count} soin${count > 1 ? 's' : ''}`;
        counter.hidden = count === 0;
      }
    }
    this.pointForm(input);
    this.showError('');
    this.sync();
    if (announce && this.offreDemandee) {
      const url = new URL(location.href);
      url.searchParams.set('offre', input.dataset.type);
      history.replaceState(history.state, '', url);
    }
    if (announce) {
      const status = this.querySelector('[data-offres-status]');
      status.classList.add('is-announcement');
      status.textContent = `Offre sélectionnée : ${input.dataset.offerName}, ${input.dataset.priceLabel}.`;
    }
  }
  pointForm(input) {
    this.form.querySelector('[name="id"]').value = input.value;
    this.form.querySelector('[name="quantity"]').value = '1';
    if (this.productForm) {
      this.productForm.dataset.productId = input.dataset.productId;
      this.productForm.dataset.productUrl = input.dataset.productUrl;
      this.productForm.dataset.quantityDefault = '1';
    }
    const animation = this.form.querySelector('add-to-cart-component');
    if (animation) animation.dataset.productVariantMedia = input.dataset.image || '';
  }
  restoreForm() {
    if (!this.restorePending) return;
    this.restorePending = false;
    clearTimeout(this.restoreTimer);
    if (this.selected) this.pointForm(this.selected);
  }
  sync() {
    const input = this.selected;
    const available = Boolean(input && !input.disabled && !this.busy);
    const main = this.form.querySelector('[name="add"]');
    const sticky = this.root.querySelector('[data-mmp-sticky-cta]');
    if (main) main.disabled = !available;
    if (sticky) sticky.disabled = !available;
    const loyalty = this.root.querySelector('[data-mmp-loyalty]');
    if (loyalty) loyalty.hidden = !available;
    if (input) {
      const stickyInput = this.seul && !this.unavailable.has(this.seul) ? this.seul : input;
      this.root.querySelectorAll('[data-mmp-cta-price]').forEach(node => {
        node.textContent = input.dataset.priceLabel;
      });
      this.root.querySelectorAll('[data-mmp-prix-sticky]').forEach(node => {
        node.textContent = stickyInput.dataset.priceLabel;
      });
      // The original SKU may be sold out while its coffret remains purchasable.
      if (main && !main.querySelector('[data-offres-cta-label]')) {
        [...main.childNodes].filter(node => node.nodeType === Node.TEXT_NODE).forEach(node => node.remove());
        const oldPrice = main.querySelector('[data-mmp-cta-price]');
        oldPrice?.remove();
        const label = document.createElement('span');
        label.dataset.offresCtaLabel = '';
        main.prepend(label);
      }
      const label = main?.querySelector('[data-offres-cta-label]');
      if (label) {
        main.classList.add('mon-offres-cta');
        label.textContent = this.dataset.cta || 'Ajouter au panier';
        let capsule = main.querySelector('[data-offres-cta-capsule]');
        if (!capsule) {
          capsule = document.createElement('span');
          capsule.dataset.offresCtaCapsule = '';
          capsule.className = 'mon-offres-cta__prices';
          capsule.innerHTML = '<s data-offres-cta-compare></s><span data-offres-cta-current></span>';
          label.after(capsule);
        }
        const compare = capsule.querySelector('[data-offres-cta-compare]');
        compare.hidden = !input.dataset.compareLabel;
        compare.textContent = input.dataset.compareLabel || '';
        capsule.querySelector('[data-offres-cta-current]').textContent = input.dataset.priceLabel;
        main.setAttribute('aria-label', `${label.textContent} : ${input.dataset.offerName}, ${input.dataset.priceLabel}`);
      }
      if (loyalty) {
        const points = Math.floor(Number(input.dataset.price) / 100);
        const value = loyalty.querySelector('[data-mmp-loyalty-points]');
        if (value) value.textContent = `${points} point${points === 1 ? '' : 's'}`;
      }
    }
  }
  setBusy(value) {
    this.busy = value;
    this.inputs.forEach(input => { input.disabled = value || this.unavailable.has(input); });
    this.setAttribute('aria-busy', String(value));
    this.sync();
  }
  showError(message) {
    const error = this.querySelector('[data-offres-error]');
    if (!error) return;
    error.textContent = message;
    error.hidden = !message;
  }
}
if (!customElements.get('mon-offres-routine')) customElements.define('mon-offres-routine', MonOffresRoutine);
